import { useEffect, useState } from "react";
import type { Business, Review } from "@/types/directory";
import RatingStars from "@/components/base/RatingStars";
import Badge from "@/components/base/Badge";
import { getBusinessReviews } from "@/services/directoryService";
import { isApiConfigured } from "@/services/http";
import ReviewForm from "./ReviewForm";
import ReportReview from "./ReportReview";

interface BusinessReviewsProps {
  business: Business;
}

export default function BusinessReviews({ business }: BusinessReviewsProps) {
  const [reviews,     setReviews]     = useState<Review[]>(business.reviews);
  const [showForm,    setShowForm]    = useState(false);
  const [pendingNote, setPendingNote] = useState(false);
  const [reportId,    setReportId]    = useState<string | null>(null);
  const [reported,    setReported]    = useState<Record<string, boolean>>({});

  // When the backend is live, hydrate the approved reviews for this business.
  useEffect(() => {
    let active = true;
    if (!isApiConfigured()) return undefined;

    getBusinessReviews(business.id)
      .then((list) => {
        if (active && list.length > 0) setReviews(list);
      })
      .catch(() => { /* keep whatever we already have */ });

    return () => {
      active = false;
    };
  }, [business.id]);

  function handleSubmitted(payload: { rating: number; authorName: string; comment: string }) {
    const optimistic: Review = {
      id:       `new-${Date.now()}`,
      author:   payload.authorName,
      rating:   payload.rating,
      date:     new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }),
      comment:  payload.comment || "—",
      verified: false,
    };
    setReviews((prev) => [optimistic, ...prev]);
    setPendingNote(true);
    setShowForm(false);
    setTimeout(() => setPendingNote(false), 6000);
  }

  return (
    <section className="rounded-lg border border-background-200 bg-[#F5F7FA] p-6 md:p-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
          Customer reviews
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-4 rounded-md border border-background-200 bg-background-100 px-5 py-3">
            <span className="font-heading text-3xl font-extrabold text-foreground-950">
              {business.rating.toFixed(1)}
            </span>
            <div>
              <RatingStars rating={business.rating} showValue={false} />
              <p className="mt-1 text-xs text-foreground-500">
                {business.reviewCount.toLocaleString("en-IN")} reviews
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className={showForm ? "ri-close-line text-base" : "ri-edit-2-line text-base"} />
            {showForm ? "Close" : "Write a review"}
          </button>
        </div>
      </div>

      {/* Moderation note for the just-submitted review */}
      {pendingNote && (
        <div className="mt-5 flex items-start gap-2.5 rounded-lg bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-time-line mt-0.5 shrink-0 text-base" />
          Your review has been submitted and is pending approval by our team.
        </div>
      )}

      {/* Review submission form */}
      {showForm && (
        <div className="mt-5">
          <ReviewForm
            businessId={Number(business.id) || 0}
            businessName={business.name}
            onSubmitted={handleSubmitted}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      <div className="mt-6 flex flex-col gap-4">
        {reviews.map((review) => (
          <article
            key={review.id}
            className="rounded-lg border border-background-200 bg-background-50 p-5"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                  {review.author.charAt(0).toUpperCase()}
                </span>
                <div>
                  <p className="font-heading text-sm font-bold text-foreground-950">
                    {review.author}
                  </p>
                  <p className="text-xs text-foreground-500">{review.date}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {review.verified ? (
                  <Badge tone="secondary" icon="ri-checkbox-circle-fill">
                    Verified visit
                  </Badge>
                ) : null}
                <RatingStars rating={review.rating} showValue={false} />
              </div>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{review.comment}</p>

            {/* Report / flag this review */}
            <div className="mt-2 flex items-center gap-3">
              {reported[review.id] ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                  <i className="ri-checkbox-circle-line text-sm" /> Thanks — this review has been reported.
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setReportId(reportId === review.id ? null : review.id)}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-xs font-medium text-foreground-500 transition-colors hover:text-rose-600"
                >
                  <i className="ri-flag-2-line text-sm" />
                  {reportId === review.id ? "Cancel report" : "Report"}
                </button>
              )}
            </div>

            {reportId === review.id && !reported[review.id] && (
              <ReportReview
                reviewId={review.id}
                onCancel={() => setReportId(null)}
                onSubmitted={() => {
                  setReported((prev) => ({ ...prev, [review.id]: true }));
                  setReportId(null);
                }}
              />
            )}
          </article>
        ))}
      </div>

      <p className="mt-5 flex items-center gap-2 text-xs text-foreground-500">
        <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-600">
          <i className="ri-shield-check-line text-[13px]" />
        </span>
        All reviews are checked by our team before they are published.
      </p>
    </section>
  );
}